/**
 * PersistentWorkspacesSection
 *
 * Settings → AI & Terminal → the list of connections whose AI workspace is
 * kept between sessions (Task T16, AI Smoothness Wave, F1), with a per-row way
 * to stop keeping one.
 *
 * **"Stop keeping" de-registers; it does NOT delete the folder.** The whole
 * point of a kept workspace is the agent notes and `.history/` inside it, so a
 * settings-list row must never be a one-click directory wipe. What it does:
 * drops the registry entry and removes the folder's `.jam-persistent` marker,
 * which makes the folder eligible for the ordinary cleanup at the next app
 * start. The row says so, and the button is labelled for what it does.
 *
 * Each row shows only what the registry itself proves — the folder name and,
 * when the connection is still saved, its display name. Nothing here stats the
 * filesystem: sizes and file counts would put disk I/O on the settings render
 * path for information the user can get by opening the folder.
 *
 * Extracted into its own file (T15's `AddSkillNudgeRow` precedent) rather than
 * growing the already-long `AiTerminalSettingsTab`.
 */

import { Button } from '../../../../components/ui/button';
import { useAppStore } from '../../../../store/appStore';
import { useConnectionStore } from '../../../connections/store';
import { useWorkspacePersistence } from '../../../terminal/hooks/useWorkspacePersistence';
import { safeConnectionName } from '../../../../types/safeConnectionDisplay';

export function PersistentWorkspacesSection() {
  // Selected raw (never `?? {}` inside the selector — a fresh object literal
  // per call is the classic zustand re-render loop). The absent-section
  // fallback is applied below instead, for the same reason the normalizer
  // defaults the section: a store slice that predates this field must render
  // an empty list, not crash the whole AI & Terminal settings tab.
  const persistentWorkspaces = useAppStore((s) => s.aiTerminalSettings.persistentWorkspaces);
  const savedConnections = useConnectionStore((s) => s.connections);
  const { setPersistence } = useWorkspacePersistence();

  const entries = persistentWorkspaces ? Object.entries(persistentWorkspaces) : [];

  // De-register only. The hook clears the folder's marker and rolls the
  // registry back if that fails — it never deletes the folder (I-4).
  const handleStopKeeping = (connectionId: string, folderName: string) => {
    void setPersistence({ connectionId, folderName, keep: false });
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Persistent workspaces</div>

      {entries.length === 0 ? (
        <div
          className="rounded-md border border-border bg-background p-4 text-xs text-muted-foreground"
          data-testid="ai-persistent-workspaces-empty"
        >
          No connections are keeping their AI workspace. Turn on &ldquo;Keep this workspace&rdquo; in an AI
          terminal&rsquo;s context bar to reuse one folder for that connection, so agent notes and{' '}
          <code className="font-mono">.history/</code> survive between sessions.
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="ai-persistent-workspaces-list">
            {entries.map(([connectionId, entry]) => {
              const connection = savedConnections.find((c) => c.id === connectionId);
              const label = safeConnectionName(connection);
              return (
                <div
                  key={connectionId}
                  className="flex items-center justify-between gap-4 rounded-md border border-border bg-background p-4"
                  data-testid={`ai-persistent-workspace-row-${connectionId}`}
                >
                  <div className="grid min-w-0 gap-0.5">
                    <div className="truncate text-sm">{label ?? entry.folderName}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">
                      {entry.folderName}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStopKeeping(connectionId, entry.folderName)}
                    data-testid={`ai-persistent-workspace-stop-${connectionId}`}
                  >
                    Stop keeping
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground">
            &ldquo;Stop keeping&rdquo; only stops Jam SQL Studio from reusing the folder — the folder and
            everything in it stay on disk, inside the workspace folder above, until you delete them
            yourself.
          </div>
        </>
      )}
    </div>
  );
}
